/**
 * `RecommendationCard` — a single recommendation rendered as a horizontal card.
 *
 * The card visualises one entry from `useRecommendations()`. It is intentionally
 * dumb: it does not know about the surrounding stack, mutations, or the demo's
 * "R-001 disappears" choreography. Enter / exit / re-order animations live one
 * level up in `RecommendationStack`, which wraps each card in `AnimatePresence`.
 *
 * Severity drives the **left edge stripe** + **pictogram chip tint** (see the
 * wiki page `Design-Specs/Recommendation-Cards.md` for the canonical table).
 * The area pictogram is looked up via `pictogramFor(rec.area)` — when
 * `rec.area` is undefined we fall back to `Sparkles` to keep the card visually
 * balanced rather than a generic contract glyph (which is the registry's
 * fallback for known-but-unmapped codes).
 *
 * The "Proč?" popover is implemented locally with Framer Motion's
 * `AnimatePresence` so we can scope the open state to the card itself — no
 * shared portal, no global overlay, no extra deps. The popover animation uses
 * a linear easing (not a spring) to stay aligned with the chunky stepped vibe.
 */
import { AnimatePresence, motion } from 'framer-motion'
import { Info, Sparkles } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { pictogramFor } from '@/components/pictograms'
import type { Recommendation, RecommendationSeverity } from '@shared/schemas'

export interface RecommendationCardProps {
  rec: Recommendation
  /**
   * Reserved for a future iteration that lets the user dismiss a card. Not
   * wired into any UI control today — kept on the props so the component's
   * surface area doesn't shift later.
   */
  onClose?: () => void
}

/**
 * Severity → PoP palette class fragments. The left-edge stripe carries the
 * severity colour, the icon chip uses a tinted background to read as the same
 * family, and the icon itself stays silhouette-dark so it reads as a chunky
 * stamped sprite. Centralised so the wiki page and the component stay in sync
 * from a single source.
 */
const SEVERITY_CLASSES: Record<
  RecommendationSeverity,
  {
    /** Left edge stripe — thicker `border-l` driven by severity. */
    edge: string
    /** Pictogram chip background — a palette-coherent tint. */
    chipBg: string
  }
> = {
  // critical uses the same severe palette as high but could be differentiated
  // with a flashing border or a distinct colour in a future design iteration.
  critical: {
    edge: 'border-l-pop-severe',
    chipBg: 'bg-pop-fire',
  },
  high: {
    edge: 'border-l-pop-severe',
    chipBg: 'bg-pop-fire',
  },
  medium: {
    edge: 'border-l-pop-caution',
    chipBg: 'bg-pop-torch',
  },
  low: {
    edge: 'border-l-pop-calm',
    chipBg: 'bg-pop-sand-light',
  },
}

export function RecommendationCard({ rec }: RecommendationCardProps): JSX.Element {
  const palette = SEVERITY_CLASSES[rec.severity]
  // Fall back to `Sparkles` when `rec.area` is undefined. `pictogramFor`
  // returns `ScrollText` for that case (a "generic contract" glyph) which
  // doesn't read as a recommendation, so we short-circuit here instead.
  const Icon = rec.area ? pictogramFor(rec.area) : Sparkles

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  // Close the popover on click outside (anywhere — card OR portaled popover)
  // and on Escape. The popover renders via createPortal to document.body to
  // escape the parent stack's `overflow-x-auto` clip (CSS computes
  // `overflow-y: auto` whenever any axis is auto, so `bottom-full` positioning
  // is invisible without a portal). Click-outside must therefore consider
  // both the card AND the popover as "inside".
  useEffect(() => {
    if (!popoverOpen) return
    function handlePointer(event: MouseEvent): void {
      const target = event.target as Node
      const inCard = cardRef.current?.contains(target)
      const inPopover = popoverRef.current?.contains(target)
      if (!inCard && !inPopover) setPopoverOpen(false)
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setPopoverOpen(false)
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [popoverOpen])

  // Compute fixed-position coords from the button's bounding rect when the
  // popover opens. Anchored to the button's top edge (popover sits above) and
  // right edge (so the popover's right aligns with the button's right edge).
  // useLayoutEffect avoids a flicker on the first paint of the popover.
  useLayoutEffect(() => {
    if (!popoverOpen || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPopoverPos({
      // 8px gap above the button — mirrors the previous `mb-2`.
      top: rect.top - 8,
      // Distance from viewport right edge to button's right edge.
      right: window.innerWidth - rect.right,
    })
  }, [popoverOpen])

  return (
    <motion.div
      ref={cardRef}
      layoutId={rec.id}
      // Compose the shared `pixel-card` utility (chunky border + stamped
      // shadow), then override the left edge to be thicker and tinted by
      // severity. The card sits on `pop-cream` from `pixel-card`.
      className={[
        'pixel-card relative flex w-[280px] shrink-0 items-center gap-3 p-3 border-l-[12px]',
        palette.edge,
      ].join(' ')}
      // `layout` lets the stack re-order smoothly when one card exits.
      layout
    >
      {/* Left: area pictogram in a chunky tinted square (no rounding — pixel feel). */}
      <div
        className={[
          'flex h-10 w-10 shrink-0 items-center justify-center border-[3px] border-pop-silhouette',
          palette.chipBg,
        ].join(' ')}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5 text-pop-silhouette" strokeWidth={2.5} />
      </div>

      {/* Middle: title (uppercase, semibold, tracking-wide) + message clamp. */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold uppercase tracking-wide leading-tight text-pop-silhouette">
          {rec.title}
        </div>
        <div
          className="mt-0.5 line-clamp-2 text-xs leading-snug text-pop-shadow"
          title={rec.message}
        >
          {rec.message}
        </div>
      </div>

      {/* Right: rule_id mono chip + "Proč?" info button. */}
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span className="bg-pop-silhouette text-pop-cream text-[10px] px-1.5 py-0.5 font-mono uppercase tracking-wider">
          {rec.rule_id}
        </span>
        <button
          ref={buttonRef}
          type="button"
          title="Proč?"
          aria-label="Proč?"
          aria-expanded={popoverOpen}
          onClick={() => setPopoverOpen((v) => !v)}
          className="p-1 text-pop-silhouette transition-colors duration-75 ease-linear hover:bg-pop-sand-light focus:outline-none focus-visible:ring-2 focus-visible:ring-pop-torch border-[2px] border-pop-silhouette"
        >
          <Info className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>

      {/* Popover — rendered via portal to document.body so the parent stack's
          `overflow-x-auto` (which forces `overflow-y: auto` per CSS spec)
          doesn't clip it. Positioned fixed from the button's bounding rect
          (computed in useLayoutEffect when popoverOpen flips true). */}
      {popoverOpen && popoverPos &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={popoverRef}
              key="popover"
              initial={{ opacity: 0, scale: 0.92, y: 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 4 }}
              transition={{ duration: 0.16, ease: 'linear' }}
              // `bottom` anchors the popover so its bottom edge sits 8 px above
              // the button (we pass `viewportHeight - rect.top + 8` via the
              // top field, but flipping to a bottom anchor keeps the popover
              // anchored on top of the button as the page scrolls).
              style={{
                position: 'fixed',
                bottom: window.innerHeight - popoverPos.top,
                right: popoverPos.right,
                zIndex: 50,
              }}
              className="pixel-card w-64 p-3 text-xs text-pop-silhouette"
              role="dialog"
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="bg-pop-silhouette text-pop-cream text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5">
                  Pravidlo {rec.rule_id}
                </span>
              </div>
              <p className="leading-snug">{rec.message}</p>
            </motion.div>
          </AnimatePresence>,
          document.body,
        )
      }
    </motion.div>
  )
}

export default RecommendationCard
